import React from 'react';
import { useAuthenticatedSocket } from '@app/hooks/useAuthenticatedSocket';
import { NotificationEnvelope } from '@app/model/Notification';
import { useNotificationStore } from '@app/store/notificationStore';

/**
 * Keeps the bell current: one socket, feeding the notification store.
 *
 * **A second connection, not a message type on the entity-update socket.** That one broadcasts every
 * entity change to every authenticated connection with no filter at all, because everything it
 * carries is safe for everyone. A notification is the opposite — it is addressed to somebody, and
 * nothing on it is safe for anyone else — so the service gives it its own endpoint, matches each
 * frame against the audience of the connection it is about to write to, and never gives this stream
 * an unfiltered send path. The cost is one more socket from the browser, which is the cheap half of
 * that trade.
 *
 * The client sends nothing. There is no subscribe protocol and no way to ask for somebody else's
 * notifications, because the server has no inbound handler to ask with — who you are was settled at
 * the handshake.
 *
 * Mounted next to EntityUpdateStream, above the router, so it lives for the whole session rather
 * than reconnecting on every navigation.
 */
export const NotificationStream: React.FC = () => {
  useAuthenticatedSocket({
    path: '/ws/notifications',

    onMessage: (event) => {
      const envelope = JSON.parse(event.data) as NotificationEnvelope;

      // The panel keys on the id and dedupes against it, so a frame without one would be a row that
      // arrives again on every push and can never be dismissed.
      if (!envelope || typeof envelope.id !== 'number') {
        console.warn('[WebSocket] Ignoring a notification with no id:', event.data);
        return;
      }

      useNotificationStore.getState().receive(envelope);
    },

    // Both the first connect and every reconnect. The push is at most once by design — the service
    // drops a frame rather than let one stalled browser hold up delivery for everybody on that pod —
    // so this is the half of the contract that makes it reliable: whatever the socket missed while
    // it was down is fetched over REST, which is the source of truth.
    onOpen: () => {
      void useNotificationStore.getState().catchUp();
    }
  });

  return null;
};
